import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import DashboardLayout from "../../Layout/DashboardLayout";
import {
  companyID,
  employeeID,
  employeeName,
  Dropdown,
  hasPrivilege,
  PRIVILEGES,
} from "../../Common/UIComponents";
import {
  fetchSetupUtilCreateData,
  fetchSetupUtilTestDetails,
  saveSetupUtilTimesheet,
  checkSetupUtilFacilityLog,
  updateSetupUtilTemperature,
  fetchSetupUtilTimesheetList,
} from "../../API_Service/LabBooking_Slice";
import ShiftChangeOverPanel from "./ShiftChangeOverForm";
import {
  fetchTempHumidityConfigByCompany,
  fetchShifts,
  fetchDateFormatConfig,
} from "../../API_Service/Settings_Slice";


const CUST_TYPES = ["Internal", "External", "OEM", "Dealer"];

const iCls =
  "w-full h-11 bg-[#f1f4f4] rounded-xl px-4 text-sm font-medium text-[#181c1d] border border-transparent focus:outline-none focus:ring-2 focus:ring-[#08757C] focus:bg-white transition-all placeholder:text-[#9eabac]";
const eiCls =
  "w-full h-10 bg-[#f1f4f4] rounded-xl px-3 text-sm font-medium text-[#181c1d] border border-transparent focus:outline-none focus:ring-2 focus:ring-[#08757C] focus:bg-white transition-all placeholder:text-[#9eabac]";
const esCls =
  "w-full h-10 bg-[#f1f4f4] rounded-xl px-3 text-sm font-medium text-[#181c1d] border border-transparent focus:outline-none focus:ring-2 focus:ring-[#08757C] focus:bg-white transition-all appearance-none cursor-pointer";

const formatDate = (dateString, format = "dd-MM-yyyy") => {
  if (!dateString) return "";
  
  const parts = dateString.split(" ");
  let datePart = parts[0];
  let timePart = parts.slice(1).join(" ");
  
  let dd, MM, yyyy, MMM;
  
  if (datePart.includes("-") && datePart.split("-")[0].length === 2) {
    const [d, m, y] = datePart.split("-");
    dd = d;
    MM = m;
    yyyy = y;
    const tempDate = new Date(`${y}-${m}-${d}T00:00:00`);
    MMM = tempDate.toLocaleString("en-US", { month: "short" });
  } else {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    dd = String(date.getDate()).padStart(2, "0");
    MM = String(date.getMonth() + 1).padStart(2, "0");
    MMM = date.toLocaleString("en-US", { month: "short" });
    yyyy = String(date.getFullYear());
    
    if (!timePart) {
      let hours = date.getHours();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; 
      const minutes = String(date.getMinutes()).padStart(2, '0');
      timePart = `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
    }
  }

  const formattedDate = format.replace("MMM", MMM).replace("MM", MM).replace("dd", dd).replace("yyyy", yyyy);
  return timePart ? `${formattedDate} ${timePart}` : formattedDate;
};


const rawId = (v) =>
  typeof v === "string" && v.startsWith("__") ? v.slice(2) : v;


function formatSlotLabel(mins) {
  const dayMins = ((mins % 1440) + 1440) % 1440;
  const h = Math.floor(dayMins / 60);
  const m = dayMins % 60;
  const period = h < 12 ? "AM" : "PM";
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${String(displayH).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
}

// Generate 30-min time slots for a full day
function generate30MinSlots() {
  const slots = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const totalMins = h * 60 + m;
      slots.push({ totalMins, label: formatSlotLabel(totalMins) });
    }
  }
  return slots;
}
const ALL_30MIN_SLOTS = [
  ...generate30MinSlots(),
  { totalMins: 1440, label: "12:00 AM" },
];

// Always leave at least this much room to drag across.
const MIN_END_WINDOW_MINS = 4 * 60;


function getEndTimeSlots(startMins) {
  if (startMins == null || startMins < 0) return [];
  const windowEnd = Math.max(1440, startMins + MIN_END_WINDOW_MINS);
  const slots = [];
  for (let mins = startMins + 30; mins <= windowEnd; mins += 30) {
    slots.push({ totalMins: mins, label: formatSlotLabel(mins) });
  }
  return slots;
}

function formatTodayDate() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function addOneDay(dateStr) {
  if (!dateStr) return dateStr;
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  if (/^\d{2}-\d{2}-\d{4}/.test(dateStr)) {
    const [dd, mm, yyyy] = dateStr.split("-").map(Number);
    const d = new Date(yyyy, mm - 1, dd);
    d.setDate(d.getDate() + 1);
    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
  }
  return dateStr;
}


function toDateKey(dateStr) {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.substring(0, 10);
  if (/^\d{2}-\d{2}-\d{4}/.test(dateStr)) {
    const [dd, mm, yyyy] = dateStr.split("-");
    return `${yyyy}-${mm}-${dd}`;
  }
  return dateStr;
}


function toMinsFromTimeStr(str) {
  if (!str) return -1;
  const [hStr, mStr] = String(str).split(":");
  const h = parseInt(hStr, 10) || 0;
  const m = parseInt(mStr, 10) || 0;
  return h * 60 + m;
}


function resolveShiftId(mins, shiftList) {
  if (mins == null || mins < 0 || !Array.isArray(shiftList) || shiftList.length === 0) {
    return null;
  }
  for (const shift of shiftList) {
    const start = toMinsFromTimeStr(shift.Shift_Start_Time);
    const end = toMinsFromTimeStr(shift.Shift_End_Time);
    if (start < 0 || end < 0) continue;
    const inWindow =
      start <= end ? mins >= start && mins < end : mins >= start || mins < end;
    if (inWindow) return shift.Shift_Timing_Id;
  }
  return null;
}

function Lbl({ children, required }) {
  return (
    <label className="block text-[10px] font-bold text-[#08757C] uppercase tracking-widest mb-1.5">
      {children}
      {required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
  );
}

function DD({
  value,
  onChange,
  options = [],
  placeholder,
  err,
  disabled,
  hideSearch,
}) {
  const ddOptions = (options ?? []).map((o) =>
    typeof o === "object"
      ? { value: `__${o.id}`, label: o.label }
      : { value: o, label: String(o) },
  );
  return (
    <Dropdown
      options={ddOptions}
      value={value ?? ""}
      onChange={(v) => onChange(v)}
      placeholder={placeholder ?? "Select"}
      searchPlaceholder="Search…"
      disabled={disabled}
      error={err ? String(err) : undefined}
      hideSearch={hideSearch}
    />
  );
}

const initials = (name) =>
  name
    ?.split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";
const AVATAR_COLORS = ["#08757C", "#7c3aed", "#0369a1", "#b45309", "#047857"];
const avatarColor = (name) =>
  AVATAR_COLORS[name?.charCodeAt(0) % AVATAR_COLORS.length] ?? "#08757C";

// Parse "dd-MM-yyyy hh:mm tt" → { dateKey: "yyyy-MM-dd", mins: number }
function parseEntryTime(startTime) {
  if (!startTime) return { dateKey: "", mins: -1 };
  const parts = startTime.trim().split(" ");
  // parts: ["dd-MM-yyyy", "hh:mm", "AM"/"PM"]
  const [dd, mm, yyyy] = (parts[0] ?? "").split("-");
  const dateKey = `${yyyy}-${mm}-${dd}`;
  const [hStr, mStr] = (parts[1] ?? "").split(":");
  let h = parseInt(hStr, 10) || 0;
  const m = parseInt(mStr, 10) || 0;
  const pm = (parts[2] ?? "").toUpperCase() === "PM";
  if (pm && h !== 12) h += 12;
  if (!pm && h === 12) h = 0;
  return { dateKey, mins: h * 60 + m };
}

function formatSepDate(dateKey) {
  if (!dateKey) return dateKey;
  // handles "yyyy-MM-dd" (ISO) or "dd-MM-yyyy"
  let d = new Date(dateKey + "T00:00:00");
  if (!isNaN(d.getTime()))
    return d.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  const [a, b, c] = dateKey.split("-").map(Number);
  d = new Date(c, b - 1, a);
  return isNaN(d.getTime())
    ? dateKey
    : d.toLocaleDateString("en-GB", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
}

function formatBlockTime(dt) {
  if (!dt) return "—";
  const d = new Date(dt);
  if (!isNaN(d.getTime())) {
    const h = d.getHours(),
      m = d.getMinutes();
    const period = h < 12 ? "AM" : "PM";
    const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${String(displayH).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
  }
  // "dd-MM-yyyy hh:mm tt" → return "hh:mm tt"
  try {
    const p = dt.trim().split(" ");
    return `${p[1]} ${p[2]}`;
  } catch {
    return dt;
  }
}

function normalizeBlock(b, idx) {
  return {
    id: `blk_${idx}`,
    startTime: formatBlockTime(b.BlockStart),
    endTime: formatBlockTime(b.BlockEnd),
    activity: b.ActivityName ?? "",
    projectNo: b.ProjectCode ?? "",
    testType: b.TestTypeName ?? "",
    testName: b.TestName ?? "",
    temp: b.Temperature?.toString() ?? "0",
    hum: b.Humidity?.toString() ?? "0",
    testOfficer: b.TestOfficer ?? "",
    testOfficer1: "",
    onBehalfBy: "",
    custType: "",
    emcTestPR: "",
    projectName: b.ProjectName ?? "",
    type: b.BlockType ?? "idle",
  };
}


function TempHumidityPopup({ facility, onSave, onClose }) {
  const [temp, setTemp] = useState("");
  const [hum, setHum] = useState("");
  const [errors, setErrors] = useState({});
  const [confirm, setConfirm] = useState(false); // out-of-range confirm state

  const tempMin = facility?.temperatureMin ?? 0;
  const tempMax = facility?.temperatureMax ?? 100;
  const humMin = facility?.humidityMin ?? 0;
  const humMax = facility?.humidityMax ?? 100;

  const validate = () => {
    const e = {};
    if (temp === "") {
      e.temp = "Temperature is required";
      return e;
    }
    if (isNaN(temp)) {
      e.temp = "Enter a valid number";
      return e;
    }
    if (hum === "") {
      e.hum = "Humidity is required";
      return e;
    }
    if (isNaN(hum)) {
      e.hum = "Enter a valid number";
      return e;
    }
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }

    const outOfRange =
      Number(temp) < tempMin ||
      Number(temp) > tempMax ||
      Number(hum) < humMin ||
      Number(hum) > humMax;

    if (outOfRange && !confirm) {
      setConfirm(true);
      return;
    }

    onSave({ temperature: temp, humidity: hum });
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-[3px] z-[200]" />
      <div className="fixed inset-0 z-[210] flex items-center justify-center p-4">
        <div
          className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
          style={{ animation: "fadeUp 0.25s cubic-bezier(0.4,0,0.2,1)" }}
        >
          <style>{`@keyframes fadeUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>

          {/* Header */}
          <div
            className="px-7 py-5 flex items-center gap-3"
            style={{ background: "linear-gradient(135deg,#08757C,#0a9aa3)" }}
          >
            <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-white text-[22px]">
                thermostat
              </span>
            </div>
            <div>
              <h3
                className="text-base font-black text-white"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Lab Temperature & Humidity
              </h3>
              <p className="text-white/60 text-xs">
                Please log current readings for this lab
              </p>
            </div>
          </div>

          <div className="px-7 py-6 space-y-4">
            {/* Out-of-range warning */}
            {confirm && (
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">
                <span
                  className="material-symbols-outlined text-amber-500 text-[20px] flex-shrink-0 mt-0.5"
                  style={{ fontVariationSettings: "'FILL' 1" }}
                >
                  warning
                </span>
                <p className="text-sm font-semibold text-amber-700">
                  Temperature or Humidity is outside the allowed range. Do you
                  want to continue anyway?
                </p>
              </div>
            )}

            {/* Temperature */}
            <div>
              <Lbl required>
                Temperature{" "}
                <span className="text-[#6e797a] normal-case font-medium">
                  (Range: {tempMin} – {tempMax} °C)
                </span>
              </Lbl>
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  className={`${iCls} ${errors.temp ? "ring-2 ring-red-300" : ""}`}
                  placeholder={`e.g. ${tempMin}`}
                  value={temp}
                  onChange={(e) => {
                    setTemp(e.target.value);
                    setErrors((v) => ({ ...v, temp: undefined }));
                    setConfirm(false);
                  }}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[11px] font-bold text-[#9eabac]">
                  °C
                </span>
              </div>
              {errors.temp && (
                <p className="text-[11px] text-red-500 mt-1">{errors.temp}</p>
              )}
            </div>

            {/* Humidity */}
            <div>
              <Lbl required>
                Humidity{" "}
                <span className="text-[#6e797a] normal-case font-medium">
                  (Range: {humMin} – {humMax} %)
                </span>
              </Lbl>
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  className={`${iCls} ${errors.hum ? "ring-2 ring-red-300" : ""}`}
                  placeholder={`e.g. ${humMin}`}
                  value={hum}
                  onChange={(e) => {
                    setHum(e.target.value);
                    setErrors((v) => ({ ...v, hum: undefined }));
                    setConfirm(false);
                  }}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[11px] font-bold text-[#9eabac]">
                  %
                </span>
              </div>
              {errors.hum && (
                <p className="text-[11px] text-red-500 mt-1">{errors.hum}</p>
              )}
            </div>
          </div>

          <div className="px-7 pb-6 flex gap-3">
            {confirm ? (
              <>
                <button
                  onClick={handleSubmit}
                  className="flex-1 flex items-center justify-center gap-2 text-white font-bold text-sm rounded-xl hover:brightness-110 transition-all"
                  style={{ background: "#f59e0b", height: "44px" }}
                >
                  <span className="material-symbols-outlined text-[17px]">
                    check
                  </span>
                  Yes, Continue
                </button>
                <button
                  onClick={() => setConfirm(false)}
                  className="px-5 h-11 text-[#3e494a] font-semibold text-sm hover:bg-[#f1f4f4] rounded-xl border border-[#e5e9e9] transition-all"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleSubmit}
                  className="flex-1 flex items-center justify-center gap-2 text-white font-bold text-sm rounded-xl hover:brightness-110 transition-all"
                  style={{ background: "#08757C", height: "44px" }}
                >
                  <span className="material-symbols-outlined text-[17px]">
                    save
                  </span>
                  Submit
                </button>
                {onClose && (
                  <button
                    onClick={onClose}
                    className="px-5 h-11 text-[#3e494a] font-semibold text-sm hover:bg-[#f1f4f4] rounded-xl border border-[#e5e9e9] transition-all"
                  >
                    Cancel
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
// h:mm AM/PM off raw minutes-from-midnight — "withSpace" toggles "6 AM" vs
// the tighter "2PM" style used right above the drag handle.
function fmtMinsShort(mins, withSpace) {
  mins = ((mins % 1440) + 1440) % 1440;
  let h = Math.floor(mins / 60);
  const m = mins % 60;
  const period = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  const sep = withSpace ? " " : "";
  return m === 0
    ? `${h}${sep}${period}`
    : `${h}:${String(m).padStart(2, "0")}${sep}${period}`;
}

function EndTimeDragPicker({
  startTime,
  startMins,
  value,
  options,
  onChange,
  onCommit,
}) {
  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const selected = options.find((o) => o.label === value) ?? null;
  const last = options[options.length - 1];
  
  const endOfDay = options.find((o) => o.totalMins === 1440) ?? last;
  const rangeStart = startMins;
  const rangeEnd = last?.totalMins ?? startMins;

  const pct = (mins) =>
    rangeEnd !== rangeStart
      ? ((mins - rangeStart) / (rangeEnd - rangeStart)) * 100
      : 0;

  const nearestOption = (clientX) => {
    if (!trackRef.current || options.length === 0) return null;
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const targetMins = rangeStart + ratio * (rangeEnd - rangeStart);
    return options.reduce((best, o) =>
      Math.abs(o.totalMins - targetMins) < Math.abs(best.totalMins - targetMins)
        ? o
        : best,
    options[0]);
  };

  const handlePointer = (clientX) => {
    const opt = nearestOption(clientX);
    if (opt) onChange(opt.label, opt.totalMins);
  };

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => {
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      handlePointer(x);
    };
    const up = () => {
      setDragging(false);
      onCommit?.();
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging, options]);

  const stepBy = (dir) => {
    if (!options.length) return;
    const idx = selected
      ? options.findIndex((o) => o.label === selected.label)
      : -1;
    const nextIdx = Math.max(
      0,
      Math.min(options.length - 1, (idx < 0 ? 0 : idx) + dir),
    );
    onChange(options[nextIdx].label, options[nextIdx].totalMins);
    onCommit?.();
  };

  const durationLabel = (() => {
    if (startMins == null || !selected) return "0h 00m";
    const total = selected.totalMins - startMins;
    if (total <= 0) return "0h 00m";
    const h = Math.floor(total / 60),
      m = total % 60;
    return `${h}h ${String(m).padStart(2, "0")}m`;
  })();


  const hourMarks = (() => {
    const span = rangeEnd - rangeStart;
    if (span <= 0) return [rangeStart];
    const count = Math.min(6, options.length);
    const raw = Array.from({ length: count }, (_, i) =>
      Math.round((rangeStart + (span * i) / (count - 1)) / 60) * 60,
    );
    return [...new Set(raw)];
  })();

  if (!startTime || options.length === 0) {
    return (
      <div className="bg-[#f7fafa] border border-[#e5e9e9] rounded-2xl px-4 py-5 text-center">
        <span className="material-symbols-outlined text-[20px] text-[#c0cbcc]">
          schedule
        </span>
        <p className="text-[12px] font-semibold text-[#9eabac] mt-1">
          {startTime
            ? "No end time slots available"
            : "Select a start time first"}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-[#f7fafa] border border-[#e5e9e9] rounded-2xl px-4 pt-3.5 pb-4">
        <div className="flex items-center justify-between mb-5 gap-3">
          <span className="flex items-center gap-1.5 text-[11.5px] font-semibold text-[#6e797a] min-w-0">
            <span className="material-symbols-outlined text-[15px] text-[#9eabac] flex-shrink-0">
              drag_indicator
            </span>
            <span className="truncate">Drag, or nudge 30 min</span>
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => stepBy(-1)}
              className="w-7 h-7 rounded-full border border-[#e5e9e9] bg-white flex items-center justify-center text-[#08757C] hover:bg-[#e4f2f2] disabled:opacity-30 disabled:cursor-not-allowed"
              disabled={!selected || options[0]?.label === selected.label}
            >
              <span className="material-symbols-outlined text-[15px]">
                remove
              </span>
            </button>
            <span className="text-[12px] font-black text-white bg-[#08757C] rounded-full px-3.5 py-1.5 tabular-nums whitespace-nowrap">
              {durationLabel}
            </span>
            <button
              type="button"
              onClick={() => stepBy(1)}
              className="w-7 h-7 rounded-full border border-[#e5e9e9] bg-white flex items-center justify-center text-[#08757C] hover:bg-[#e4f2f2] disabled:opacity-30 disabled:cursor-not-allowed"
              disabled={
                !selected ||
                options[options.length - 1]?.label === selected.label
              }
            >
              <span className="material-symbols-outlined text-[15px]">
                add
              </span>
            </button>
            {endOfDay && (
              <button
                type="button"
                onClick={() => {
                  onChange(endOfDay.label, endOfDay.totalMins);
                  onCommit?.();
                }}
                title="Set End Time to the end of the day"
                className={`flex items-center gap-1 text-[11px] font-bold rounded-full px-3 py-1.5 border transition-all whitespace-nowrap ${
                  selected?.label === endOfDay.label
                    ? "bg-[#08757C] border-[#08757C] text-white"
                    : "bg-white border-[#e5e9e9] text-[#3e494a] hover:border-[#08757C]"
                }`}
              >
                <span className="material-symbols-outlined text-[13px]">
                  event_available
                </span>
                Full Day
              </button>
            )}
          </div>
        </div>

        <div
          ref={trackRef}
          className="relative h-11 select-none touch-none mx-1"
          onMouseDown={(e) => {
            handlePointer(e.clientX);
            setDragging(true);
          }}
          onTouchStart={(e) => {
            handlePointer(e.touches[0].clientX);
            setDragging(true);
          }}
        >
          <div className="absolute top-[19px] left-0 right-0 h-[5px] rounded-full bg-[#e5e9e9]" />

          {options.map((o) => {
            const isActive = selected?.label === o.label;
            const isHour = o.totalMins % 60 === 0;
            return (
              <div
                key={o.label}
                className="absolute -translate-x-1/2 pointer-events-none transition-all"
                style={{
                  left: `${pct(o.totalMins)}%`,
                  top: isActive ? "10px" : isHour ? "13px" : "15px",
                  width: isActive ? "2.5px" : isHour ? "2px" : "1.5px",
                  height: isActive ? "14px" : isHour ? "9px" : "6px",
                  borderRadius: "2px",
                  background: isActive
                    ? "#08757C"
                    : isHour
                      ? "#9eabac"
                      : "#c7d1d1",
                }}
              />
            );
          })}

          <div
            className="absolute top-[19px] h-[5px] rounded-full bg-[#08757C]"
            style={{
              left: "0%",
              width: selected ? `${pct(selected.totalMins)}%` : 0,
            }}
          />

          <div
            className="absolute top-[21px] left-0 -translate-x-1/2 -translate-y-1/2 pointer-events-none flex flex-col items-center"
          >
            <span className="text-[9px] font-bold text-[#6e797a] bg-white border border-[#e5e9e9] rounded-md px-1.5 py-0.5 mb-1 whitespace-nowrap">
              Start
            </span>
            <span className="w-2 h-2 rounded-full bg-[#3e494a] border-2 border-white" />
          </div>

          {selected && (
            <div
              className="absolute top-[21px] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
              style={{ left: `${pct(selected.totalMins)}%` }}
            >
              <span className="text-[10px] font-black text-[#08757C] mb-1 whitespace-nowrap">
                {fmtMinsShort(selected.totalMins, false)}
              </span>
              <div
                className="w-8 h-8 rounded-full bg-white border-[3px] border-[#08757C] cursor-grab active:cursor-grabbing shadow-sm flex items-center justify-center"
              >
                <span className="material-symbols-outlined text-[13px] text-[#08757C]">
                  drag_indicator
                </span>
              </div>
            </div>
          )}
        </div>

   
        <div className="relative h-4 mx-1 mt-2">
          {hourMarks.map((m) => (
            <span
              key={m}
              className="absolute -translate-x-1/2 text-[9.5px] font-bold text-[#9eabac] tabular-nums whitespace-nowrap"
              style={{ left: `${pct(m)}%` }}
            >
              {fmtMinsShort(m, true)}
            </span>
          ))}
        </div>

        <p className="text-center text-[10px] text-[#9eabac] mt-3">
          Window shown: {startTime} – {last.label} &nbsp;·&nbsp; grid snaps to
          :00 / :30
        </p>
      </div>

    </div>
  );
}

function TimeSlotPicker({
  startDate,
  startTime,
  endDate,
  endTime,
  onChangeEndTime,
  onChangeEndDate,
  onEndTimeCommit,
  noShift,
  endTimeError,
  dateFormat = "dd-MM-yyyy",
}) {
  const startMins =
    ALL_30MIN_SLOTS.find((s) => s.label === startTime)?.totalMins ?? -1;
  const endOptions =
    startMins >= 0
      ? noShift
        ? ALL_30MIN_SLOTS.filter((s) => s.totalMins > startMins)
        : getEndTimeSlots(startMins)
      : [];

  const parsePickerDate = (str) => {
    if (!str) return null;
    if (/^\d{4}-\d{2}-\d{2}/.test(str))
      return new Date(str.substring(0, 10) + "T00:00:00");
    if (/^\d{2}-\d{2}-\d{4}/.test(str)) {
      const [dd, mm, yyyy] = str.split("-");
      return new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    }
    return null;
  };

  const durationLabel = (() => {
    if (!startTime || !endTime) return null;
    const sMin =
      ALL_30MIN_SLOTS.find((s) => s.label === startTime)?.totalMins ?? 0;

    const eMin =
      endOptions.find((s) => s.label === endTime)?.totalMins ??
      ALL_30MIN_SLOTS.find((s) => s.label === endTime)?.totalMins ??
      0;
    let total = eMin - sMin;
    if (noShift && startDate && endDate) {
      const start = parsePickerDate(startDate);
      const end = parsePickerDate(endDate);
      if (start && end && !isNaN(start) && !isNaN(end)) {
        const dayDiff = Math.round((end - start) / 86400000);
        total += dayDiff * 1440;
      }
    }
    if (!Number.isFinite(total) || total <= 0) return null;
    const h = Math.floor(total / 60),
      m = total % 60;
    return h > 0
      ? m > 0
        ? `${h}h ${m}m`
        : `${h} hr${h > 1 ? "s" : ""}`
      : `${m} min`;
  })();

  return (
    <div className="space-y-4">
      {/* Start Time — readonly, from API */}
      <div>
        <p className="text-[10px] font-black text-[#08757C] uppercase tracking-widest mb-2">
          Start Time <span className="text-red-400">*</span>
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Lbl>Date</Lbl>
            <input
              type="text"
              className={`${iCls} opacity-70 cursor-not-allowed`}
              value={startDate ? formatDate(startDate, dateFormat) : "—"}
              readOnly
            />
          </div>
          <div>
            <Lbl>Time</Lbl>
            <input
              type="text"
              className={`${iCls} opacity-70 cursor-not-allowed`}
              value={startTime || "—"}
              readOnly
            />
          </div>
        </div>
      </div>

      <div>
        <p className="text-[10px] font-black text-[#08757C] uppercase tracking-widest mb-2">
          Drag to Set End Time
        </p>
        <EndTimeDragPicker
          startTime={startTime}
          startMins={startMins >= 0 ? startMins : null}
          value={endTime}
          options={endOptions}
          onChange={onChangeEndTime}
          onCommit={onEndTimeCommit}
        />
      </div>

      {/* End Time */}
      <div>
        <p className="text-[10px] font-black text-[#08757C] uppercase tracking-widest mb-2">
          End Time <span className="text-red-400">*</span>
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Lbl>Date</Lbl>
            {noShift ? (
              <div className="relative">
                <input
                  type="date"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  value={(() => {
                    if (!endDate) return "";
                    if (/^\d{4}-\d{2}-\d{2}/.test(endDate))
                      return endDate.substring(0, 10);
                    if (/^\d{2}-\d{2}-\d{4}/.test(endDate)) {
                      const [dd, mm, yyyy] = endDate.split("-");
                      return `${yyyy}-${mm}-${dd}`;
                    }
                    return "";
                  })()}
                  min={(() => {
                    if (!startDate) return "";
                    if (/^\d{4}-\d{2}-\d{2}/.test(startDate))
                      return startDate.substring(0, 10);
                    if (/^\d{2}-\d{2}-\d{4}/.test(startDate)) {
                      const [dd, mm, yyyy] = startDate.split("-");
                      return `${yyyy}-${mm}-${dd}`;
                    }
                    return "";
                  })()}
                  onChange={(e) => onChangeEndDate?.(e.target.value)}
                  onKeyDown={(e) => e.preventDefault()}
                  onClick={(e) => e.currentTarget.showPicker?.()}
                />
                <input
                  type="text"
                  className={`${iCls} bg-white pointer-events-none`}
                  value={endDate ? formatDate(endDate, dateFormat) : "—"}
                  readOnly
                />
              </div>
            ) : (
              <input
                type="text"
                className={`${iCls} opacity-70 cursor-not-allowed`}
                value={endDate ? formatDate(endDate, dateFormat) : "—"}
                readOnly
              />
            )}
          </div>
          <div>
            <Lbl required>Time</Lbl>
      
            <DD
              value={endTime || ""}
              onChange={(v) => {
                const opt = endOptions.find((o) => o.label === v);
                onChangeEndTime?.(v, opt?.totalMins);
                onEndTimeCommit?.();
              }}
              options={endOptions.map((o) => o.label)}
              placeholder={
                startTime ? "Select end time" : "Select a start time first"
              }
              err={endTimeError}
              disabled={!startTime || endOptions.length === 0}
              hideSearch
            />
          </div>
        </div>
      </div>

      {/* Duration badge */}
      {durationLabel && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
          <span className="material-symbols-outlined text-emerald-500 text-[15px]">
            timer
          </span>
          <span className="text-[12px] font-bold text-emerald-700">
            Duration: {durationLabel}
          </span>
        </div>
      )}
    </div>
  );
}

// =============================================================
// ADD SETUP DRAWER
// =============================================================
function SetupDrawer({
  lab,
  createData,
  onClose,
  onSaved,
  tempLogged,
  onNeedTempLog,
  timesheetList = [],
  editEntry = null,
  adhocTesting = false,
  dateFormat = "dd-MM-yyyy",
}) {
  const dispatch = useDispatch();
  const facilityID = lab?.facilityId ?? lab?.id ?? 0;
  const scheduleSectionRef = useRef(null);
  const officersSectionRef = useRef(null);

  // Dropdown options
  const projectOptions = useMemo(
    () =>
      (createData?.projects ?? []).map((p) => ({
        id: p.projectId,
        label: p.projectName,
        bookingNo: p.bookingNo ?? null,
      })),
    [createData],
  );
  const activityOptions = useMemo(
    () =>
      (createData?.activities ?? []).map((a) => ({
        id: a.activityId,
        label: a.activityName,
      })),
    [createData],
  );
  const employeeOptions = useMemo(
    () =>
      (createData?.employees ?? []).map((e) => ({
        id: e.employeeId,
        label: e.employeeName,
      })),
    [createData],
  );
  const traineeOptions = useMemo(
    () =>
      (createData?.traineeEmployees ?? []).map((e) => ({
        id: e.employeeId,
        label: e.employeeName,
      })),
    [createData],
  );

  // Test Type / Test Name cascade
  const [testTypeOptions, setTestTypeOptions] = useState([]);
  const [testTypeLoading, setTestTypeLoading] = useState(false);
  const [testNameOptions, setTestNameOptions] = useState([]);
  const [testNameLoading, setTestNameLoading] = useState(false);
  const [selectedBookingNo, setSelectedBookingNo] = useState(() => {
    const first = timesheetList.find((t) => t.BookingNo ?? t.bookingNo);
    return first?.BookingNo ?? first?.bookingNo ?? null;
  });

  useEffect(() => {
    if (!createData || !facilityID) return;
    setTestTypeLoading(true);
    dispatch(fetchSetupUtilTestDetails({ facilityID, companyID, projectID: 0 }))
      .then((res) => {
        const data = res?.payload?.data ?? res?.payload ?? {};
        setTestTypeOptions(
          (data.testType ?? []).map((t) => ({
            id: t.testTypeID,
            label: t.testTypeName,
          })),
        );
      })
      .finally(() => setTestTypeLoading(false));
  }, [createData, facilityID, dispatch]); // eslint-disable-line

  const buildEmptyForm = useCallback(() => {
    if (editEntry) {
      const parsePart = (str, part) => {
        if (!str) return "";
        const p = str.trim().split(" ");
        if (part === "time") return p.length >= 3 ? `${p[1]} ${p[2]}` : "";
        if (part === "date") {
          const d = p[0] ?? "";
          if (/^\d{2}-\d{2}-\d{4}/.test(d)) {
            const [dd, mm, yyyy] = d.split("-");
            return `${yyyy}-${mm}-${dd}`;
          }
          return d;
        }
        return "";
      };
      return {
        projectId: editEntry.ProjectID ? `__${editEntry.ProjectID}` : "",
        testTypeId: editEntry.TestTypeID ? `__${editEntry.TestTypeID}` : "",
        testNameId: editEntry.Project_Test_Det_Id
          ? `__${editEntry.Project_Test_Det_Id}`
          : "",
        activityId: editEntry.ActivityID ? `__${editEntry.ActivityID}` : "",
        startDate: parsePart(editEntry.StartTime, "date"),
        startTime: parsePart(editEntry.StartTime, "time"),
        endDate: parsePart(editEntry.EndTime, "date"),
        endTime: parsePart(editEntry.EndTime, "time"),
        temperature: editEntry.Temperature?.toString() ?? "",
        humidity: editEntry.Humidity?.toString() ?? "",
        remarks: editEntry.Remarks ?? "",
        tqeEmpId:
          editEntry.TraineeEmpID && editEntry.TraineeEmpID > 0
            ? `__${editEntry.TraineeEmpID}`
            : "",
        onBehalfEmpId:
          editEntry.OnBehalfEmpID && editEntry.OnBehalfEmpID > 0
            ? `__${editEntry.OnBehalfEmpID}`
            : "",
      };
    }
    // Create mode
    const rawStartTime = createData?.startTime ?? "";
    const timeParts = rawStartTime.trim().split(" ");
    const timeOnly =
      timeParts.length >= 3 ? `${timeParts[1]} ${timeParts[2]}` : "12:00 AM";
    return {
      projectId: "",
      testTypeId: "",
      testNameId: "",
      activityId: "",
      startDate: createData?.startDate ?? formatTodayDate(),
      startTime: timeOnly,
      endDate: createData?.startDate ?? formatTodayDate(),
      endTime: "",
      temperature: createData?.facility?.temperature?.toString() ?? "",
      humidity: createData?.facility?.humidity?.toString() ?? "",
      remarks: "",
      tqeEmpId: "",
      onBehalfEmpId: "",
    };
  }, [createData, editEntry]);

  const [form, setForm] = useState(buildEmptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      setForm(buildEmptyForm());
      isFirstMount.current = false;
    } else if (!editEntry) {
      // After save in create mode: refresh time fields only
      const fresh = buildEmptyForm();
      setForm((prev) => ({
        ...prev,
        startDate: fresh.startDate,
        startTime: fresh.startTime,
        endDate: fresh.endDate,
        endTime: "",
      }));
    }
  }, [buildEmptyForm]); // eslint-disable-line

  // Pre-load test names when opening in edit mode
  useEffect(() => {
    if (!editEntry?.ProjectID || !facilityID || editEntry.ProjectID <= 0)
      return;
    setTestNameLoading(true);
    dispatch(
      fetchSetupUtilTestDetails({
        facilityID,
        companyID,
        projectID: editEntry.ProjectID,
      }),
    )
      .then((res) => {
        const data = res?.payload?.data ?? res?.payload ?? {};
        setTestNameOptions(
          (data.testName ?? []).map((t) => ({
            id: t.projectTestDetID,
            label: t.testName,
          })),
        );
      })
      .finally(() => setTestNameLoading(false));
  }, [editEntry?.ProjectID, facilityID, dispatch]); // eslint-disable-line

  
  useEffect(() => {
    if (createData?.facility?.temperature != null) {
      setForm((prev) => ({
        ...prev,
        temperature: createData.facility.temperature?.toString() ?? "",
        humidity: createData.facility.humidity?.toString() ?? "",
      }));
    }
  }, [createData?.facility?.temperature, createData?.facility?.humidity]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const isInternalProject = Number(rawId(form.projectId)) === -1;

  const isNoShift = useMemo(() => {
    const id = rawId(form.activityId);
    return (
      activityOptions
        .find((a) => String(a.id) === String(id))
        ?.label?.toLowerCase()
        .trim() === "no shift"
    );
  }, [activityOptions, form.activityId]);

  const handleActivityChange = (v) => {
    const id = rawId(v);
    const actName =
      activityOptions
        .find((a) => String(a.id) === String(id))
        ?.label?.toLowerCase()
        .trim() ?? "";
    setForm((f) => ({
      ...f,
      activityId: v,
      ...(actName !== "no shift" ? { endDate: f.startDate } : {}),
    }));
    setErrors((e) => ({ ...e, activityId: undefined, endTime: undefined }));

   
    setTimeout(() => {
      scheduleSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 0);
  };

  const handleProjectChange = (rawVal) => {
    set("projectId", rawVal);
    set("testNameId", "");
    setTestNameOptions([]);
    const projectID = Number(rawId(rawVal));
    const match = projectOptions.find((p) => Number(p.id) === projectID);
    const tsmatch = timesheetList.find(
      (t) => Number(t.ProjectID ?? t.Project_Id ?? 0) === projectID,
    );
    setSelectedBookingNo(
      match?.bookingNo ?? tsmatch?.BookingNo ?? tsmatch?.bookingNo ?? null,
    );
    if (!projectID || projectID <= 0) return;
    setTestNameLoading(true);
    dispatch(fetchSetupUtilTestDetails({ facilityID, companyID, projectID }))
      .then((res) => {
        const data = res?.payload?.data ?? res?.payload ?? {};
        setTestNameOptions(
          (data.testName ?? []).map((t) => ({
            id: t.projectTestDetID,
            label: t.testName,
          })),
        );
      })
      .finally(() => setTestNameLoading(false));
  };

  const validate = () => {
    const e = {};
    if (isNoShift && !isInternalProject)
      e.activityId = "No Shift can only be selected for Internal Projects";
    if (!form.endTime) e.endTime = "Required";
    return e;
  };

  const submit = async () => {
    if (!tempLogged) {
      onNeedTempLog();
      return;
    }
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setSaving(true);

    try {
      debugger;

      const resolvedStartTime = form.startTime || "12:00 AM";

      const resolvedEndDate = form.endDate;
      const onBehalfId = rawId(form.onBehalfEmpId);
      const traineeId = rawId(form.tqeEmpId);

      const payload = {
        employeeID: employeeID,
        projectID: Number(rawId(form.projectId)) || 0,
        testTypeID: Number(rawId(form.testTypeId)) || 0,
        project_Test_Det_Id: Number(rawId(form.testNameId)) || 0,
        facilityID: facilityID,
        activityID: Number(rawId(form.activityId)) || 0,
        startTime: `${form.startDate} ${resolvedStartTime}`,
        endTime: `${resolvedEndDate} ${form.endTime}`,
        temperature: Number(form.temperature) || 0,
        humidity: Number(form.humidity) || 0,
        ...(onBehalfId ? { onBehalfEmpID: Number(onBehalfId) } : {}),
        ...(traineeId ? { onTraineeEmpID: Number(traineeId) } : {}),
        timesheetID: editEntry?.TimesheetID ?? editEntry?.timesheetID ?? 0,
        remarks: form.remarks ?? "",
        companyID: companyID,
      };

      console.log("🔍 DEBUG - Final payload being sent to API:", payload);

      const res = await dispatch(saveSetupUtilTimesheet(payload));

      if (res?.payload?.success) {
        if (editEntry) {
          setSaved(true);
          setTimeout(() => {
            setSaved(false);
            onSaved(null, null, 0);
            onClose();
          }, 1200);
        } else {
          
          const refreshed = await dispatch(
            fetchSetupUtilCreateData({
              facilityID,
              companyID,
              employeeID,
              adhoc: adhocTesting,
            }),
          );
          const refreshPayload = refreshed?.payload?.data ?? {};
          const refreshData = refreshPayload.data ?? refreshPayload;
          const newStartTime = refreshPayload.startTime ?? null;
          const newStartDate = refreshPayload.startDate ?? null;

          const extractTimeOnly = (raw) => {
            if (!raw) return "";
            const parts = raw.trim().split(" ");
            return parts.length >= 3 ? `${parts[1]} ${parts[2]}` : raw;
          };

          setForm((prev) => ({
            ...prev,
            startDate: newStartDate ?? prev.startDate,
            startTime: extractTimeOnly(newStartTime),
            endDate: newStartDate ?? prev.startDate,
            endTime: "",
          }));

          setSaved(true);
          setTimeout(() => setSaved(false), 2500);

          const savedTotalHours = res?.payload?.data?.totalHours ?? 0;
          onSaved(
            newStartTime,
            newStartDate,
            Number(savedTotalHours),
            refreshData,
          );
        }
      }
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/25 backdrop-blur-[2px] z-[60]"
        onClick={onClose}
      />
      <div
        className="fixed top-0 right-0 h-full w-[520px] bg-white z-[70] flex flex-col"
        style={{
          animation: "slideIn 0.3s cubic-bezier(0.4,0,0.2,1)",
          boxShadow: "-8px 0 40px rgba(0,0,0,0.12)",
        }}
      >
        <style>{`@keyframes slideIn{from{transform:translateX(100%)}to{transform:translateX(0)}}`}</style>

        {/* Header */}
        <div
          className="px-5 pt-4 pb-3 flex-shrink-0"
          style={{ background: "linear-gradient(135deg,#08757C,#0a9aa3)" }}
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="material-symbols-outlined text-white/70 text-[18px]">
                  {editEntry ? "edit" : "add_task"}
                </span>
                <span className="text-white/70 text-xs font-semibold uppercase tracking-widest">
                  {editEntry ? "Edit Activity" : "New Activity"}
                </span>
              </div>
              <h2
                className="text-xl font-black text-white"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                {editEntry ? "Edit Activity" : "Add New Activity"}
              </h2>
              <p className="text-white/60 text-xs mt-1">
                for{" "}
                <span className="text-white font-semibold">
                  {lab?.facilityName ??
                    lab?.labName ??
                    createData?.facility?.facilityName}
                </span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="h-9 w-9 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <span className="material-symbols-outlined text-[20px]">
                close
              </span>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2.5 bg-[#f7fafa]">
          {!createData ? (
            <div className="flex items-center justify-center py-12 text-[#9eabac]">
              <span className="material-symbols-outlined text-[28px] animate-spin mr-2">
                progress_activity
              </span>
              <span className="text-sm">Loading form data...</span>
            </div>
          ) : (
            <>
              {/* Booking No */}
              {selectedBookingNo && (
                <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-2xl px-4 py-3">
                  <span className="material-symbols-outlined text-[#08757C] text-[18px]">
                    confirmation_number
                  </span>
                  <span className="text-[11px] font-black text-[#08757C] uppercase tracking-widest">
                    Booking No
                  </span>
                  <span className="text-[13px] font-black text-[#181c1d] ml-1">
                    {selectedBookingNo}
                  </span>
                </div>
              )}

              {/* Test Details */}
              <div className="bg-white rounded-2xl p-3.5 space-y-2.5 shadow-sm border border-[#e5e9e9]">
                <p className="text-[11px] font-black text-[#08757C] uppercase tracking-widest">
                  Test Details
                </p>

                <div>
                  <Lbl>Project</Lbl>
                  <DD
                    value={form.projectId}
                    onChange={handleProjectChange}
                    options={projectOptions}
                    placeholder="Select an active project"
                  />
                </div>

                <div>
                  <Lbl>Test Type</Lbl>
                  <div className="relative">
                    <DD
                      value={form.testTypeId}
                      onChange={(v) => set("testTypeId", v)}
                      options={testTypeOptions}
                      placeholder={
                        testTypeLoading ? "Loading..." : "Select test type"
                      }
                      disabled={testTypeLoading}
                    />
                    {testTypeLoading && (
                      <span className="material-symbols-outlined absolute right-8 top-1/2 -translate-y-1/2 text-[#9eabac] text-[16px] animate-spin pointer-events-none">
                        progress_activity
                      </span>
                    )}
                  </div>
                </div>

                {!isInternalProject && (
                  <div>
                    <Lbl>Test Name</Lbl>
                    <div className="relative">
                      <DD
                        value={form.testNameId}
                        onChange={(v) => set("testNameId", v)}
                        options={testNameOptions}
                        placeholder={
                          !form.projectId
                            ? "Select a project first"
                            : testNameLoading
                              ? "Loading..."
                              : testNameOptions.length === 0
                                ? "No test names found"
                                : "Select test name"
                        }
                        disabled={!form.projectId || testNameLoading}
                      />
                      {testNameLoading && (
                        <span className="material-symbols-outlined absolute right-8 top-1/2 -translate-y-1/2 text-[#9eabac] text-[16px] animate-spin pointer-events-none">
                          progress_activity
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <Lbl>Activity</Lbl>
                  <DD
                    value={form.activityId}
                    onChange={handleActivityChange}
                    options={activityOptions}
                    placeholder="Select activity"
                    err={errors.activityId}
                  />
                </div>
              </div>

              {/* Schedule — Date + 30-min time slots */}
              <div
                ref={scheduleSectionRef}
                className="bg-white rounded-2xl p-3.5 shadow-sm border border-[#e5e9e9]"
              >
                <p className="text-[11px] font-black text-[#08757C] uppercase tracking-widest mb-1.5">
                  Schedule
                </p>
                <TimeSlotPicker
                  startDate={form.startDate}
                  startTime={form.startTime}
                  endDate={form.endDate}
                  endTime={form.endTime}
                  onChangeEndTime={(v, totalMins) => {
               
                    setForm((f) => {
                      if (isNoShift) return { ...f, endTime: v };
                      const crossesMidnight =
                        totalMins != null && totalMins >= 1440;
                      return {
                        ...f,
                        endTime: v,
                        endDate: crossesMidnight
                          ? addOneDay(f.startDate)
                          : f.startDate,
                      };
                    });
                    setErrors((e) => ({ ...e, endTime: undefined }));
                  }}
                  onChangeEndDate={(v) => {
                    set("endDate", v);
            
                    setTimeout(() => {
                      officersSectionRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });
                    }, 0);
                  }}
                  noShift={isNoShift}
                  endTimeError={errors.endTime ? "End time is required." : null}
                  dateFormat={dateFormat}
                />
              </div>

              {/* Environment — pre-filled from facility, READONLY (matches MVC) */}
              <div className="bg-white rounded-2xl p-3.5 space-y-2.5 shadow-sm border border-[#e5e9e9]">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-black text-[#08757C] uppercase tracking-widest">
                    Environment
                  </p>
                  <div className="flex items-center gap-1.5">
                    <span
                      className="material-symbols-outlined text-[14px]"
                      style={{ color: "#08757C" }}
                    >
                      lock
                    </span>
                    <span className="text-[10px] text-[#6e797a] font-semibold">
                      Auto-filled from facility
                    </span>
                  </div>
                </div>
                {createData?.facility?.temperatureMin != null && (
                  <p className="text-[11px] text-[#6e797a]">
                    Allowed: {createData.facility.temperatureMin}–
                    {createData.facility.temperatureMax}°C &nbsp;|&nbsp;
                    {createData.facility.humidityMin}–
                    {createData.facility.humidityMax}% RH
                  </p>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Lbl>Temperature</Lbl>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.1"
                        className={`${iCls} opacity-70 cursor-not-allowed`}
                        value={form.temperature}
                        readOnly
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[11px] font-bold text-[#9eabac]">
                        °C
                      </span>
                    </div>
                  </div>
                  <div>
                    <Lbl>Humidity</Lbl>
                    <div className="relative">
                      <input
                        type="number"
                        className={`${iCls} opacity-70 cursor-not-allowed`}
                        value={form.humidity}
                        readOnly
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[11px] font-bold text-[#9eabac]">
                        %
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-[10px] text-[#9eabac]">
                  To update readings, use the{" "}
                  <span className="font-bold text-[#08757C]">
                    Log Temp/Humidity
                  </span>{" "}
                  button on the main page.
                </p>
              </div>

              {/* Officers */}
              <div
                ref={officersSectionRef}
                className="bg-white rounded-2xl p-3.5 space-y-2.5 shadow-sm border border-[#e5e9e9]"
              >
                <p className="text-[11px] font-black text-[#08757C] uppercase tracking-widest">
                  Officers
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Lbl>TO(Test officer)</Lbl>
                    <input
                      className={`${iCls} opacity-70 cursor-not-allowed`}
                      value={employeeName}
                      readOnly
                    />
                  </div>
                  <div>
                    <Lbl>TO1</Lbl>
                    <DD
                      value={form.tqeEmpId}
                      onChange={(v) => set("tqeEmpId", v)}
                      options={traineeOptions}
                      placeholder="Select"
                    />
                  </div>
                  <div className="col-span-2">
                    <Lbl>On Behalf Of</Lbl>
                    {(() => {
                      const traineeId = Number(rawId(form.tqeEmpId)) || null;
                      const filteredEmployees = employeeOptions.filter(
                        (e) => e.id !== employeeID && e.id !== traineeId,
                      );
                      return (
                        <DD
                          value={form.onBehalfEmpId}
                          onChange={(v) => set("onBehalfEmpId", v)}
                          options={filteredEmployees}
                          placeholder="Select"
                        />
                      );
                    })()}
                  </div>
                </div>
                <div>
                  <Lbl>Remarks</Lbl>
                  <textarea
                    className={`${iCls} h-16 py-2.5 resize-none`}
                    placeholder="Additional notes..."
                    value={form.remarks}
                    onChange={(e) => set("remarks", e.target.value)}
                    maxLength={600}
                  />
                  {form.remarks?.length > 0 && (
                    <div
                      className={`flex items-center gap-1 mt-1 text-xs ${form.remarks.length === 600 ? "text-red-500 font-semibold" : "text-[#6e797a]"}`}
                    >
                      <span>{form.remarks.length}/600</span>
                      {form.remarks.length === 600 && (
                        <span className="text-red-500">— limit reached</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-2.5 bg-white border-t border-[#e5e9e9] flex gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 h-11 text-[#3e494a] font-semibold text-sm hover:bg-[#f1f4f4] rounded-xl border border-[#e5e9e9] transition-all"
          >
            Cancel
          </button>

          <button
            onClick={submit}
            disabled={saving || !createData}
            className="flex-1 flex items-center justify-center gap-2 text-white font-bold text-sm rounded-xl hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-60"
            style={{ background: "#08757C", height: "44px" }}
          >
            <span className="material-symbols-outlined text-[18px]">
              {editEntry ? "save" : "add_task"}
            </span>
            {saving
              ? "Saving..."
              : editEntry
                ? "Update Activity"
                : "Create Activity"}
          </button>
        </div>
      </div>

      {saved && (
        <div
          className="fixed top-6 left-1/2 -translate-x-1/2 z-[300] flex items-center gap-2.5 bg-emerald-500 text-white px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold pointer-events-none"
          style={{ animation: "fadeUp 0.25s cubic-bezier(0.4,0,0.2,1)" }}
        >
          <span
            className="material-symbols-outlined text-[18px]"
            style={{ fontVariationSettings: "'FILL' 1" }}
          >
            check_circle
          </span>
          {editEntry
            ? "Activity Updated successfully!"
            : "Activity created successfully!"}
        </div>
      )}
    </>
  );
}

// =============================================================
// PROJECT DETAIL MODAL
// =============================================================
const SCHEDULE_BLOCK_LABELS = [
  "12:00 AM – 04:00 AM",
  "04:00 AM – 08:00 AM",
  "08:00 AM – 12:00 PM",
  "12:00 PM – 04:00 PM",
  "04:00 PM – 08:00 PM",
  "08:00 PM – 12:00 AM",
];

function getEntryBlockIndex(startTimeStr) {
  if (!startTimeStr) return 0;
  const parts = startTimeStr.trim().split(" ");
  if (parts.length < 3) return 0;
  let h = parseInt(parts[1].split(":")[0], 10);
  const isPM = parts[2].toUpperCase() === "PM";
  if (isPM && h !== 12) h += 12;
  if (!isPM && h === 12) h = 0;
  return Math.floor(h / 4);
}

function ProjectDetailModal({
  proj,
  lab,
  entryList = [],
  createData,
  onClose,
  onEntrySaved,
}) {
  const entries = useMemo(() => {
    const projID = Number(proj.ProjectID); // null/undefined → 0 = no-project
    return entryList.filter((t) => {
      if (Number(t.ProjectID) !== projID) return false;
      
      if (projID === 0 && !t.EmployeeID) return false;
      return true;
    });
  }, [entryList, proj.ProjectID]);

  const [filterDate, setFilterDate] = useState(formatTodayDate);

  const filteredEntries = useMemo(() => {
    if (!filterDate) return entries;
    return entries.filter((e) => {
      if (!e.StartTime) return false;
      const datePart = e.StartTime.trim().split(" ")[0]; // "dd-MM-yyyy"
      if (/^\d{2}-\d{2}-\d{4}/.test(datePart)) {
        const [dd, mm, yyyy] = datePart.split("-");
        return `${yyyy}-${mm}-${dd}` === filterDate;
      }
      return e.StartTime.startsWith(filterDate);
    });
  }, [entries, filterDate]);

  const grouped = useMemo(() => {
    const g = {};
    filteredEntries.forEach((e) => {
      const idx = getEntryBlockIndex(e.StartTime);
      if (!g[idx]) g[idx] = [];
      g[idx].push(e);
    });
    return g;
  }, [filteredEntries]);

  const blockIndices = useMemo(
    () =>
      Object.keys(grouped)
        .map(Number)
        .sort((a, b) => a - b),
    [grouped],
  );

  const [expandedBlocks, setExpandedBlocks] = useState(() => new Set());

  const [editEntry, setEditEntry] = useState(null);

  const shiftDate = (dir) => {
    // If the date was cleared, restart from today instead of producing NaN
    const base = filterDate ? new Date(filterDate + "T00:00:00") : new Date();
    const d = isNaN(base.getTime()) ? new Date() : base;
    d.setDate(d.getDate() + dir);
    setFilterDate(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
    );
  };

  const fmtFilterDate = filterDate
    ? new Date(filterDate + "T00:00:00").toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "dd-mm-yyyy";

  const toggleBlock = (idx) => {
    setExpandedBlocks((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-[4px] z-[60]"
        onClick={onClose}
      />
      <div
        className="fixed inset-3 md:inset-6 z-[70] rounded-3xl overflow-hidden flex flex-col"
        style={{
          background: "#f7fafa",
          animation: "fadeUp 0.3s cubic-bezier(0.4,0,0.2,1)",
          boxShadow: "0 32px 80px rgba(0,0,0,0.2)",
        }}
      >
        <style>{`@keyframes fadeUp{from{transform:translateY(30px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>

        {/* Header */}
        <div
          className="flex-shrink-0 px-6 py-4 flex items-center gap-4"
          style={{ background: "linear-gradient(135deg,#08757C,#0a9aa3)" }}
        >
          <div className="h-14 w-14 rounded-2xl bg-white/15 flex items-center justify-center flex-shrink-0">
            <span className="material-symbols-outlined text-white text-[28px]">
              science
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h2
              className="text-xl font-black text-white truncate"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              {proj.ProjectName || "—"}
            </h2>
            <p className="text-white/60 text-xs mt-0.5 truncate">
              {proj.ProjectCode} · {lab?.facilityName ?? lab?.labName} ·{" "}
              {proj.TestTypeName}
            </p>
            {proj.BookingNo && (
              <div className="flex items-center gap-1.5 mt-1">
                <span className="material-symbols-outlined text-white/60 text-[13px]">
                  confirmation_number
                </span>
                <span className="text-white/70 text-[11px] font-semibold">
                  {proj.BookingNo}
                </span>
              </div>
            )}
          </div>
          <div className="flex-shrink-0 text-center">
            <p className="text-2xl font-black text-white">{entries.length}</p>
            <p className="text-white/60 text-[10px] font-semibold uppercase tracking-widest">
              Entries
            </p>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors flex-shrink-0"
          >
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        {/* Sub-header */}
        <div className="flex-shrink-0 px-6 py-2.5 bg-white border-b border-[#e5e9e9] flex items-center gap-3">
          <span
            className="material-symbols-outlined text-[16px]"
            style={{ color: "#08757C" }}
          >
            list_alt
          </span>
          <p className="text-sm font-extrabold text-[#181c1d]">
            Schedule Entries
          </p>
          <div className="ml-auto flex items-center gap-3">
            <div className="flex items-center bg-white border border-[#e5e9e9] rounded-2xl shadow-sm overflow-hidden">
              <button
                onClick={() => shiftDate(-1)}
                className="h-9 w-9 flex items-center justify-center hover:bg-[#f1f4f4] transition-colors border-r border-[#e5e9e9]"
              >
                <span
                  className="material-symbols-outlined text-[18px]"
                  style={{ color: "#08757C" }}
                >
                  chevron_left
                </span>
              </button>
              <div className="relative flex items-center gap-2 px-4 h-9 cursor-pointer">
                <span
                  className="material-symbols-outlined text-[14px] pointer-events-none"
                  style={{ color: "#08757C" }}
                >
                  calendar_today
                </span>
                <span className="text-[13px] font-bold text-[#181c1d] whitespace-nowrap pointer-events-none">
                  {fmtFilterDate}
                </span>
                <input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full"
                />
              </div>
              <button
                onClick={() => shiftDate(1)}
                className="h-9 w-9 flex items-center justify-center hover:bg-[#f1f4f4] transition-colors border-l border-[#e5e9e9]"
              >
                <span
                  className="material-symbols-outlined text-[18px]"
                  style={{ color: "#08757C" }}
                >
                  chevron_right
                </span>
              </button>
            </div>
            <span className="text-[11px] text-[#9eabac] font-medium">
              {filteredEntries.length} record
              {filteredEntries.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Entry list — grouped by 4-hour block */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {filteredEntries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-[#9eabac]">
              <span className="material-symbols-outlined text-[48px] mb-3">
                event_available
              </span>
              <p className="text-sm font-bold">
                No entries for {fmtFilterDate}
              </p>
              <p className="text-xs mt-1">
                Use prev / next to browse other dates.
              </p>
            </div>
          ) : (
            blockIndices.map((idx) => {
              const blockEntries = grouped[idx] ?? [];
              const isOpen = expandedBlocks.has(idx);
              return (
                <div
                  key={idx}
                  className="rounded-2xl border border-[#e5e9e9] overflow-hidden bg-white shadow-sm"
                >
                  {/* Block header — clickable to toggle */}
                  <button
                    onClick={() => toggleBlock(idx)}
                    className="w-full flex items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-[#f7fafa]"
                    style={{ background: isOpen ? "#eef8f8" : "white" }}
                  >
                    <div
                      className="h-8 w-8 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors"
                      style={{ background: isOpen ? "#08757C" : "#f1f4f4" }}
                    >
                      <span
                        className="material-symbols-outlined text-[16px]"
                        style={{ color: isOpen ? "white" : "#6e797a" }}
                      >
                        nest_clock_farsight_analog
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-extrabold text-[#181c1d]">
                        {SCHEDULE_BLOCK_LABELS[idx] ?? `Block ${idx + 1}`}
                      </p>
                      <p className="text-[11px] text-[#9eabac] font-medium">
                        {blockEntries.length} entr
                        {blockEntries.length !== 1 ? "ies" : "y"}
                      </p>
                    </div>
                    <span
                      className="material-symbols-outlined text-[20px] text-[#9eabac] transition-transform duration-200"
                      style={{
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    >
                      expand_more
                    </span>
                  </button>

                  {/* Collapsible entries */}
                  {isOpen && (
                    <div className="border-t border-[#e5e9e9] divide-y divide-[#f1f4f4]">
                      {blockEntries.map((entry, i) => (
                        <div
                          key={entry.TimesheetID ?? i}
                          className="px-5 py-4 hover:bg-[#fafcfc] transition-colors"
                        >
                          <div className="flex flex-wrap items-center gap-4">
                            {/* Time range */}
                            <div className="flex-shrink-0 min-w-[130px]">
                              <div className="flex items-center gap-1">
                                <span className="material-symbols-outlined text-[13px] text-[#9eabac]">
                                  schedule
                                </span>
                                <span className="text-sm font-extrabold text-[#181c1d]">
                                  {formatBlockTime(entry.StartTime)}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 mt-0.5">
                                <span className="material-symbols-outlined text-[13px] text-[#9eabac]">
                                  arrow_downward
                                </span>
                                <span className="text-sm font-extrabold text-[#181c1d]">
                                  {formatBlockTime(entry.EndTime)}
                                </span>
                              </div>
                            </div>

                            {/* Activity + Test info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-[#181c1d] truncate">
                                {entry.ActivityName || "—"}
                              </p>
                              <p className="text-[11px] text-[#6e797a] truncate mt-0.5">
                                {[entry.TestTypeName, entry.TestName]
                                  .filter(Boolean)
                                  .join(" · ") || "No test details"}
                              </p>
                            </div>

                            {/* Officer avatar */}
                            {entry.TestOfficer && (
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <div
                                  className="h-8 w-8 rounded-full flex items-center justify-center text-white text-[10px] font-black flex-shrink-0"
                                  style={{
                                    background: avatarColor(entry.TestOfficer),
                                  }}
                                >
                                  {initials(entry.TestOfficer)}
                                </div>
                                <span className="text-[11px] font-semibold text-[#6e797a] hidden lg:block max-w-[100px] truncate">
                                  {entry.TestOfficer}
                                </span>
                              </div>
                            )}

                            {/* Temp / Humidity chip */}
                            {(entry.Temperature != null ||
                              entry.Humidity != null) && (
                              <div className="flex items-center gap-1.5 bg-[#f1f4f4] rounded-xl px-3 py-1.5 flex-shrink-0">
                                <span className="material-symbols-outlined text-[14px] text-[#6e797a]">
                                  thermostat
                                </span>
                                <span className="text-[11px] font-semibold text-[#3e494a]">
                                  {entry.Temperature ?? 0}°C /{" "}
                                  {entry.Humidity ?? 0}%
                                </span>
                              </div>
                            )}

                            {/* Edit button */}
                            {hasPrivilege(PRIVILEGES.SU_EDIT) && (
                              <button
                                onClick={() => setEditEntry(entry)}
                                className="flex-shrink-0 flex items-center gap-1.5 text-[11px] font-bold px-4 py-2 rounded-xl border transition-all hover:shadow-sm active:scale-[0.97]"
                                style={{
                                  background: "#08757C12",
                                  color: "#08757C",
                                  borderColor: "#08757C30",
                                }}
                              >
                                <span className="material-symbols-outlined text-[14px]">
                                  edit
                                </span>
                                Edit
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-3 bg-white border-t border-[#e5e9e9]">
          <p className="text-[11px] text-[#9eabac] font-medium">
            {proj.ProjectCode} · {entries.length} entr
            {entries.length !== 1 ? "ies" : "y"}
          </p>
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-xl border border-[#e5e9e9] hover:bg-[#f1f4f4] text-[#3e494a] transition-all"
          >
            <span className="material-symbols-outlined text-[15px]">close</span>
            Close
          </button>
        </div>
      </div>

      {editEntry && createData && (
        <SetupDrawer
          lab={lab}
          createData={createData}
          editEntry={editEntry}
          tempLogged={true}
          onNeedTempLog={() => {}}
          onClose={() => setEditEntry(null)}
          onSaved={() => {
            setEditEntry(null);
            onEntrySaved?.();
          }}
        />
      )}
    </>
  );
}

// =============================================================
// MAIN PAGE
// =============================================================
export default function LabUtilizationIndex() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const selectedLab = location.state?.lab;
  const createDataFromNav = location.state?.createData;
  const startTimeFromNav = location.state?.startTime ?? null;
  const startDateFromNav = location.state?.startDate ?? null;
  const timesheetDataFromNav = location.state?.timesheetData;


  const [toast, setToast] = useState(null);
  const showToast = (message, isError = false) => {
    setToast({ message, isError });
    setTimeout(() => setToast(null), 3500);
  };

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [changeoverOpen, setChangeoverOpen] = useState(false);
  const [viewProject, setViewProject] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [selectedDate, setSelectedDate] = useState(formatTodayDate);
  const [adhocTesting, setAdhocTesting] = useState(false);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [timesheetList, setTimesheetList] = useState(
    timesheetDataFromNav?.SetupList ?? [],
  );
  const [blockList, setBlockList] = useState(
    timesheetDataFromNav?.BlockList ?? [],
  ); // eslint-disable-line
  const [entryList, setEntryList] = useState(
    timesheetDataFromNav?.EntryList ?? [],
  );

  const [createData, setCreateData] = useState(
    createDataFromNav
      ? {
          ...createDataFromNav,
          startTime: startTimeFromNav,
          startDate: startDateFromNav,
        }
      : null,
  );

  const selectedLabId = selectedLab?.facilityId ?? selectedLab?.id;
  const [viewChangeoverId, setViewChangeoverId] = useState(null);
  
  const todayKey = new Date().toISOString().split("T")[0]; // "yyyy-MM-dd"
  const storageKey = selectedLabId
    ? `facilityLog_${selectedLabId}_${todayKey}`
    : null;
  // Accumulated testing hours since last temperature capture (resets on temp save, persists in sessionStorage)
  const accHrsKey = selectedLabId
    ? `tempAccHrs_${selectedLabId}_${todayKey}`
    : null;
 
  const tempLoggedDayKey = selectedLabId
    ? `tempLoggedDay_${selectedLabId}`
    : null;

  const tempLoggedShiftKey = selectedLabId
    ? `tempLoggedShift_${selectedLabId}`
    : null;

  const readFromStorage = () => {
    if (!storageKey) return null;
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const writeToStorage = (temperature, humidity) => {
    if (!storageKey) return;
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({ temperature, humidity, logged: true }),
      );
    } catch {
      /* ignore quota errors */
    }
  };

  const storedLog = readFromStorage();
  const [tempLogged, setTempLogged] = useState(storedLog?.logged === true);
  const [showTempPopup, setShowTempPopup] = useState(false);
  
  const [tempHumidityFrequency, setTempHumidityFrequency] = useState(null);
  const [loggedTemp, setLoggedTemp] = useState(
    storedLog
      ? { temperature: storedLog.temperature, humidity: storedLog.humidity }
      : { temperature: "", humidity: "" },
  );
  const tempIntervalRef = useRef(null);
  const refreshTimesheetRef = useRef(null);
  const selectedDateRef = useRef(selectedDate);
  const datePickerRef = useRef(null);

  const currentWorkDateRef = useRef(null);

  const currentWorkShiftRef = useRef(null);
  const [shiftList, setShiftList] = useState([]);
  const [userDateFormat, setUserDateFormat] = useState("dd-MM-yyyy");

  useEffect(() => {
    dispatch(fetchDateFormatConfig({ companyID }))
      .unwrap()
      .then((res) => {
        const data = res?.data ?? res;
        const config = Array.isArray(data) ? data[0] : data;
        if (config?.DateFormat) {
          setUserDateFormat(config.DateFormat);
        }
      })
      .catch(console.error);
  }, [dispatch]);


  useEffect(() => {
    dispatch(fetchTempHumidityConfigByCompany({ companyId: companyID }))
      .then((res) => {
        const code = res?.payload?.data?.frequencyCode;
        if (code) setTempHumidityFrequency(code);
      })
      .catch(() => {});
  }, [dispatch]);

 
  useEffect(() => {
    if (!selectedLabId) return;
  
    if (tempHumidityFrequency == null || tempHumidityFrequency === "NONE")
      return;
    if (!tempLogged) setShowTempPopup(true);
  }, [selectedLabId, tempHumidityFrequency]);

  useEffect(() => {
    dispatch(fetchShifts({ ID: 0, companyID }))
      .then((res) => {
        const rows = res?.payload?.data;
        if (Array.isArray(rows)) setShiftList(rows);
      })
      .catch(() => {});
  }, [dispatch]);

 
  useEffect(() => {
    if (!storedLog || !createData?.facility) return;
    setCreateData((prev) => ({
      ...prev,
      facility: {
        ...prev.facility,
        temperature: Number(storedLog.temperature),
        humidity: Number(storedLog.humidity),
      },
    }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  
  useEffect(() => {
    if (!selectedLabId || tempHumidityFrequency === "NONE") return;

    const checkFacilityLog = () => {
      dispatch(
        checkSetupUtilFacilityLog({
          facID: selectedLabId,
          employeeID,
          companyID,
        }),
      ).then((res) => {
        const raw = res?.payload?.data;
        const alreadyLogged = raw === 0 || raw === "0";
        if (!alreadyLogged) {
         
          if (storageKey) sessionStorage.removeItem(storageKey);
          setTempLogged(false);
          setLoggedTemp({ temperature: "", humidity: "" });
          setShowTempPopup(true);
        } else {
          if (!readFromStorage()) {
     
            if (tempHumidityFrequency === "4HRS") {
              const facilityTemp = Number(createData?.facility?.temperature ?? 0);
              const facilityHum = Number(createData?.facility?.humidity ?? 0);
              if (facilityTemp === 0 && facilityHum === 0) {
                setShowTempPopup(true);
              } else {
                setTempLogged(true);
              }
            }
          }
        }
      });
    };

    checkFacilityLog(); // run immediately on mount (mirrors MVC page load)
    tempIntervalRef.current = setInterval(checkFacilityLog, 10000);

    return () => clearInterval(tempIntervalRef.current);
  }, [selectedLabId, tempHumidityFrequency]); // eslint-disable-line

  const handleTempSave = async ({ temperature, humidity }) => {
    const facID = selectedLab?.facilityId ?? selectedLab?.id ?? 0;
    const res = await dispatch(
      updateSetupUtilTemperature({
        facID,
        temperature: Number(temperature),
        humidity: Number(humidity),
        employeeID,
        companyID,
      }),
    );

    if (!res?.payload?.success) {
      alert("Failed to save temperature. Please try again.");
      return;
    }

    writeToStorage(temperature, humidity);
    setLoggedTemp({ temperature, humidity });
    setTempLogged(true);
    setShowTempPopup(false);
    // Reset accumulated hours counter — next 4-hour window starts from here
    if (accHrsKey) sessionStorage.setItem(accHrsKey, 0);
    
    if (tempLoggedDayKey) {
      sessionStorage.setItem(
        tempLoggedDayKey,
        toDateKey(currentWorkDateRef.current ?? selectedDate),
      );
    }
    
    
    if (tempLoggedShiftKey) {
      const shiftIdToWrite =
        currentWorkShiftRef.current ??
        resolveShiftId(parseEntryTime(createData?.startTime).mins, shiftList);
      if (shiftIdToWrite != null) {
        sessionStorage.setItem(tempLoggedShiftKey, shiftIdToWrite);
      }
    }
    if (createData?.facility) {
      setCreateData((prev) => ({
        ...prev,
        facility: {
          ...prev.facility,
          temperature: Number(temperature),
          humidity: Number(humidity),
        },
      }));
    }
  };

  
  const groupedProjects = useMemo(() => {
    const map = new Map();
    for (const row of timesheetList) {
      const key = row.ProjectID != null ? row.ProjectID : "no-project";
      if (map.has(key)) {
        const ex = map.get(key);
        if (row.EndTime > ex.EndTime) ex.EndTime = row.EndTime;
      } else {
        map.set(key, { ...row });
      }
    }
    return Array.from(map.values());
  }, [timesheetList]);

 
  const allSlots = useMemo(() => {
    if (!selectedDate) return [];
    const entryMap = new Map();
    entryList.forEach((e) => {
      const { dateKey, mins } = parseEntryTime(e.StartTime);
      if (dateKey === selectedDate && mins >= 0) entryMap.set(mins, e);
    });
    const slots = [];
    for (let i = 0; i < 48; i++) {
      const sMin = i * 30,
        eMin = (i + 1) * 30;
      const sH = Math.floor(sMin / 60),
        sM = sMin % 60;
      const eH = Math.floor(eMin / 60),
        eM = eMin % 60;
      slots.push(
        entryMap.get(sMin) ?? {
          _empty: true,
          StartTime: `${selectedDate}T${String(sH).padStart(2, "0")}:${String(sM).padStart(2, "0")}:00`,
          EndTime: `${selectedDate}T${String(eH % 24).padStart(2, "0")}:${String(eM).padStart(2, "0")}:00`,
        },
      );
    }
    return slots;
  }, [selectedDate, entryList]);

  const filteredTimesheetList = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allSlots;
    // When searching, show only matching filled rows (no empty slots)
    return entryList.filter(
      (p) =>
        (p.TestName ?? "").toLowerCase().includes(q) ||
        (p.ProjectCode ?? "").toLowerCase().includes(q) ||
        (p.ProjectName ?? "").toLowerCase().includes(q) ||
        (p.ActivityName ?? "").toLowerCase().includes(q),
    );
  }, [allSlots, entryList, search]);

  const tableRows = useMemo(() => {
    const rows = [];
    let prevDateKey = "";
    filteredTimesheetList.forEach((item, idx) => {
      // Empty slots have ISO StartTime; real entries have "dd-MM-yyyy hh:mm tt"
      let dateKey;
      if (item._empty) {
        const dt = new Date(item.StartTime);
        dateKey = !isNaN(dt.getTime())
          ? `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`
          : "";
      } else {
        dateKey = parseEntryTime(item.StartTime).dateKey;
      }
      if (dateKey !== prevDateKey) {
        rows.push({ type: "sep", dateKey });
        prevDateKey = dateKey;
      }
      const sNo = idx + 1;
      rows.push({
        type: "entry",
        item,
        sNo,
        isShiftStart: sNo === 1 || (sNo - 1) % 8 === 0,
        isChangeover: !!(
          item.ShiftchangeOver_ID && item.ShiftchangeOver_ID !== 0
        ),
      });
    });
    return rows;
  }, [filteredTimesheetList]);

  const totalProjects = groupedProjects.length;

  const handleOpenDrawer = useCallback(() => {
    if (!selectedLab) return;
    setDrawerOpen(true);

    
    if (!tempLogged) setShowTempPopup(true);

 
    dispatch(
      fetchSetupUtilCreateData({
        facilityID: selectedLab.facilityId ?? selectedLab.id,
        companyID,
        employeeID,
        adhoc: adhocTesting,
      }),
    ).then((res) => {
      const payload = res?.payload?.data ?? {};
      const data = payload.data ?? payload;
      const startTime = payload.startTime ?? null;
      const startDate = payload.startDate ?? null;
      setCreateData({ ...data, startTime, startDate });
    });
  }, [selectedLab, adhocTesting, dispatch, tempLogged]);

  const shiftDate = (dir) => {
    // If the date was cleared, restart from today instead of producing NaN
    const base = selectedDate
      ? new Date(selectedDate + "T00:00:00")
      : new Date();
    const d = isNaN(base.getTime()) ? new Date() : base;
    d.setDate(d.getDate() + dir);
    setSelectedDate(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
    );
  };

  const refreshTimesheetList = () => {
    const facID = selectedLab?.facilityId ?? selectedLab?.id ?? 0;
    if (!facID) return;
    dispatch(
      fetchSetupUtilTimesheetList({
        facilityID: facID,
        employeeID,
        companyID,
        selDate: selectedDateRef.current,
      }),
    ).then((res) => {

      const outer = res?.payload?.data ?? {};
      const data = outer.data ?? outer;
      const setupList = data.SetupList ?? data.setupList;
      const blockListData = data.BlockList ?? data.blockList;
      const entryListData = data.EntryList ?? data.entryList;
      if (setupList) setTimesheetList(setupList);
      if (blockListData) setBlockList(blockListData);
      if (entryListData) setEntryList(entryListData);
    });
  };

  refreshTimesheetRef.current = refreshTimesheetList;
  selectedDateRef.current = selectedDate;

  useEffect(() => {
    if (!selectedLabId) return;
    setTimesheetList([]);
    setBlockList([]);
    setEntryList([]);
    refreshTimesheetRef.current?.();
  }, [selectedDate]); // eslint-disable-line

  
  useEffect(() => {
    if (!selectedLabId) return;
    refreshTimesheetRef.current?.();
    const id = setInterval(() => {
      refreshTimesheetRef.current?.();
    }, 120000);
    return () => clearInterval(id);
  }, [selectedLabId]); // eslint-disable-line

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    refreshTimesheetList();
  };

  const handleSaved = () => {
    setDrawerOpen(false);
    refreshTimesheetList();
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen" style={{ background: "#f0f4f4" }}>
        {/* Page Header */}
        <div className="px-6 pt-3 pb-3">
          <div className="flex items-center justify-between mb-2">
            <nav className="flex items-center gap-2 text-xs text-[#6e797a]">
              <span>Lab Management</span>
              <span className="material-symbols-outlined text-[13px]">
                chevron_right
              </span>
              <span>Setup Utilization</span>
              <span className="material-symbols-outlined text-[13px]">
                chevron_right
              </span>
              <span className="font-semibold" style={{ color: "#08757C" }}>
                {selectedLab?.facilityName ?? "Lab"}
              </span>
            </nav>
            <button
              onClick={() => navigate("/lab-management/setup-lab-utilization")}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-xl border border-[#e5e9e9] bg-white hover:bg-[#f0f4f4] hover:border-[#08757C]/30 transition-all text-[#3e494a]"
            >
              <span className="material-symbols-outlined text-[14px]">
                west
              </span>
              Back to Labs
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1
                className="text-xl font-black text-[#181c1d] tracking-tight"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Setup Utilization
              </h1>
              <p className="text-[#6e797a] text-sm mt-0.5">
                view projects · manage test blocks
              </p>
            </div>
            <div className="flex items-stretch gap-2">
              {/* Stats cluster */}
              <div className="flex items-center bg-white rounded-2xl border border-[#e5e9e9] shadow-sm overflow-hidden">
                <div className="px-3 py-1.5 flex items-center justify-center gap-1.5 min-w-[64px] h-full">
                  <p className="text-xl font-black text-emerald-600 leading-none">
                    {totalProjects}
                  </p>
                  <p className="text-[10px] font-bold text-[#6e797a] uppercase tracking-wide">
                    Projects
                  </p>
                </div>
              </div>

              {/* Utility cluster */}
              <div className="flex items-center bg-white rounded-2xl border border-[#e5e9e9] shadow-sm divide-x divide-[#eef1f1] overflow-hidden">
                {/* MP Button */}
                <button
                  onClick={() =>
                    navigate("/lab-management/measurement-protocol")
                  }
                  title="Go to Measurement Protocol"
                  className="flex items-center gap-2 px-3 py-1.5 hover:bg-[#f0f4f4] transition-all group"
                >
                  <div className="w-7 h-7 rounded-xl bg-[#08757C]/10 flex items-center justify-center text-[#08757C] group-hover:scale-105 transition-transform">
                    <span className="material-symbols-outlined text-[16px]">
                      assignment
                    </span>
                  </div>
                  <div className="text-left">
                    <p className="text-[10px] font-bold text-[#6e797a] uppercase tracking-wide leading-none">
                      View
                    </p>
                    <p className="text-xs font-black text-[#08757C] leading-none mt-0.5">
                      MP
                    </p>
                  </div>
                </button>

                {/* Adhoc Testing toggle */}
                <button
                  type="button"
                  onClick={() => setAdhocTesting((v) => !v)}
                  title={
                    adhocTesting
                      ? "Adhoc testing enabled"
                      : "Enable adhoc testing"
                  }
                  className={`flex items-center gap-2.5 px-3 py-1.5 transition-all
                    ${adhocTesting ? "bg-[#08757C]/8" : "hover:bg-[#f0f4f4]"}`}
                >
                  <span
                    className={`material-symbols-outlined text-[18px] ${adhocTesting ? "text-[#08757C]" : "text-[#9eabac]"}`}
                  >
                    bolt
                  </span>
                  <div className="text-left">
                    <p
                      className={`text-[10px] font-bold uppercase tracking-wide leading-none ${adhocTesting ? "text-[#08757C]" : "text-[#6e797a]"}`}
                    >
                      Adhoc Testing
                    </p>
                    <p
                      className={`text-[9px] font-semibold leading-none mt-0.5 ${adhocTesting ? "text-[#08757C]/70" : "text-[#9eabac]"}`}
                    >
                      {adhocTesting ? "ON" : "OFF"}
                    </p>
                  </div>
                  {/* Toggle pill */}
                  <span
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0
                      ${adhocTesting ? "bg-[#08757C]" : "bg-[#cdd6d7]"}`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform
                        ${adhocTesting ? "translate-x-[18px]" : "translate-x-[3px]"}`}
                    />
                  </span>
                </button>
              </div>

              {/* Primary actions */}
              {selectedLab && hasPrivilege(PRIVILEGES.SU_CREATE) && (
                <>
                  <button
                    onClick={() => setChangeoverOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-2xl font-bold text-sm shadow-sm border-2 active:scale-[0.98] transition-all"
                    style={{
                      background: "rgba(8,117,124,0.06)",
                      borderColor: "#08757C",
                      color: "#08757C",
                    }}
                  >
                    <span className="material-symbols-outlined text-[18px]">
                      swap_horiz
                    </span>
                    Shift Change Over
                  </button>

                  <button
                    onClick={handleOpenDrawer}
                    className="flex items-center gap-2 text-white px-4 py-2 rounded-2xl font-bold text-sm shadow-md hover:brightness-110 active:scale-[0.98] transition-all"
                    style={{
                      background: "linear-gradient(135deg,#08757C,#0a9aa3)",
                    }}
                  >
                    <span className="material-symbols-outlined text-[18px]">
                      add
                    </span>
                    Add New Activity
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pb-4">
          {selectedLab && (
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-3">
                <div
                  className="h-9 w-9 rounded-2xl flex items-center justify-center text-white font-black text-sm flex-shrink-0"
                  style={{
                    background: "linear-gradient(135deg,#08757C,#0a9aa3)",
                  }}
                >
                  {(selectedLab.facilityName ?? selectedLab.labName ?? "")
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
                <div>
                  <h2 className="text-base font-extrabold text-[#181c1d]">
                    {selectedLab.facilityName ?? selectedLab.labName}
                  </h2>
                  <p className="text-xs text-[#6e797a]">
                    {selectedLab.location}
                  </p>
                </div>
              </div>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#9eabac] text-[16px]">
                  search
                </span>
                <input
                  className="h-8 bg-white border border-[#e5e9e9] rounded-xl pl-9 pr-4 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-[#08757C] w-48 placeholder:text-[#9eabac] transition-all"
                  placeholder="Search projects..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          )}

          
          <div className="flex items-center mb-2.5">
            <div className="flex-1 flex items-center">
              {selectedLab && (
                <span
                  className="inline-flex items-center gap-2.5 rounded-xl pl-3 pr-4 py-1.5 border shadow-sm"
                  style={{ background: "#eafde0", borderColor: "#7be85a" }}
                >
                  <span className="h-8 w-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                    <span
                      className="material-symbols-outlined text-[17px]"
                      style={{ color: "#2e9b1f" }}
                    >
                      swap_horiz
                    </span>
                  </span>
                  <div className="text-left">
                    <p
                      className="text-[10px] font-bold uppercase tracking-wide leading-none"
                      style={{ color: "#2e9b1f" }}
                    >
                      Row Color
                    </p>
                    <p className="text-[12px] font-extrabold leading-none mt-1 text-[#1d5a12]">
                      Shift Change Over Entry
                    </p>
                  </div>
                </span>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => shiftDate(-1)}
                className="h-8 w-8 flex items-center justify-center rounded-xl border border-[#e5e9e9] bg-white hover:bg-[#f0f4f4] hover:border-[#08757C]/30 transition-all shadow-sm"
              >
                <span className="material-symbols-outlined text-[20px] text-[#08757C]">
                  chevron_left
                </span>
              </button>

              <div
                className="relative flex items-center gap-2 px-4 py-1.5 border border-[#08757C]/30 rounded-xl bg-white shadow-sm cursor-pointer select-none"
                onClick={() => datePickerRef.current?.showPicker()}
              >
                <span className="material-symbols-outlined text-[15px] text-[#08757C] pointer-events-none">
                  calendar_today
                </span>
                <span className="text-sm font-bold text-[#181c1d] pointer-events-none">
                  {selectedDate
                    ? (() => {
                        const d = new Date(selectedDate + "T00:00:00");
                        const ddMMyyyy = `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
                        return formatDate(ddMMyyyy, userDateFormat);
                      })()
                    : "dd-mm-yyyy"}
                </span>
                <input
                  ref={datePickerRef}
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full"
                />
              </div>

              <button
                onClick={() => shiftDate(1)}
                className="h-8 w-8 flex items-center justify-center rounded-xl border border-[#e5e9e9] bg-white hover:bg-[#f0f4f4] hover:border-[#08757C]/30 transition-all shadow-sm"
              >
                <span className="material-symbols-outlined text-[20px] text-[#08757C]">
                  chevron_right
                </span>
              </button>

              <span className="text-sm font-bold text-[#3e494a] ml-1 whitespace-nowrap">
                {selectedDate
                  ? new Date(selectedDate + "T00:00:00").toLocaleDateString(
                      "en-US",
                      { weekday: "long" },
                    )
                  : ""}
              </span>
            </div>

            <div className="flex-1" />
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-24 text-[#9eabac]">
              <span className="material-symbols-outlined text-[32px] animate-spin">
                progress_activity
              </span>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-[#e5e9e9] shadow-sm overflow-hidden">
              <div
                className="overflow-auto"
                style={{ maxHeight: "calc(100vh - 300px)" }}
              >
                {search && filteredTimesheetList.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-[#9eabac]">
                    <span className="material-symbols-outlined text-[48px] mb-3">
                      search_off
                    </span>
                    <p className="text-sm font-extrabold text-[#181c1d]">
                      No matching entries
                    </p>
                    <p className="text-xs text-[#6e797a] mt-1">
                      Try a different search term.
                    </p>
                  </div>
                ) : (
                  <table
                    className="w-full border-collapse"
                    style={{ fontSize: "11px" }}
                  >
                    <thead>
                      <tr
                        className="sticky top-0 z-10"
                        style={{
                          background: "linear-gradient(135deg,#08757C,#0a9aa3)",
                        }}
                      >
                        {[
                          "S.No",
                          "Start Time",
                          "End Time",
                          "Project No.",
                          "Test Type",
                          "Test Name",
                          "Activity",
                          "Temp",
                          "Hum",
                          "Test Officer",
                          "Test Officer1",
                          "On Behalf Of",
                          "",
                          "Project Name",
                          "Remarks",
                        ].map((h, i) => (
                          <th
                            key={i}
                            className="px-2 py-1.5 text-left text-white font-black text-[9px] uppercase tracking-widest whitespace-nowrap"
                            style={{
                              borderRight: "1px solid rgba(255,255,255,0.15)",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableRows.map((row, idx) => {
                        if (row.type === "sep") {
                          return (
                            <tr key={`sep-${idx}`}>
                              <td
                                colSpan={15}
                                className="px-3 py-1.5 text-center text-xs font-extrabold text-[#08757C] border-y border-[#e5e9e9]"
                                style={{ background: "#eef8f8" }}
                              >
                                <span
                                  className="material-symbols-outlined text-[13px] align-middle mr-1"
                                  style={{ verticalAlign: "middle" }}
                                >
                                  calendar_today
                                </span>
                                {formatSepDate(row.dateKey)}
                              </td>
                            </tr>
                          );
                        }
                        const { item, sNo, isShiftStart, isChangeover } = row;
                        
                        const rowBg = isChangeover
                          ? "#9bff77"
                          : sNo % 2 === 0
                            ? "#fafcfc"
                            : "white";
                        const td =
                          "px-2 py-1 border-b border-[#f1f4f4] whitespace-nowrap";
                        const hasLoggedData =
                          item.TimesheetID != null &&
                          !!(
                            item.ProjectCode ||
                            item.TestTypeName ||
                            item.TestName ||
                            item.ActivityName ||
                            item.TestOfficer ||
                            item.OnBehalfEmpName ||
                            item.OnTraineeEmpName
                          );
                        return (
                          <tr
                            key={item.TimesheetID ?? idx}
                            style={{ background: rowBg }}
                            className="hover:brightness-[0.97] transition-all"
                          >
                            <td
                              className={`${td} font-black text-center ${isShiftStart ? "text-amber-800" : "text-[#c0cbcc]"}`}
                              style={
                                isShiftStart
                                  ? { background: "#fde68a" }
                                  : undefined
                              }
                            >
                              {sNo}
                            </td>
                            <td
                              className={`${td} font-semibold ${isChangeover ? "text-[#08757C] cursor-pointer" : "text-[#181c1d]"}`}
                              onClick={
                                isChangeover
                                  ? () =>
                                      setViewChangeoverId(
                                        item.ShiftchangeOver_ID,
                                      )
                                  : undefined
                              }
                            >
                              {isChangeover && (
                                <span
                                  className="material-symbols-outlined text-[11px] mr-0.5"
                                  style={{ verticalAlign: "middle" }}
                                >
                                  swap_horiz
                                </span>
                              )}
                              {formatBlockTime(item.StartTime)}
                            </td>
                            <td
                              className={`${td} font-semibold ${isChangeover ? "text-[#08757C] cursor-pointer" : "text-[#181c1d]"}`}
                              onClick={
                                isChangeover
                                  ? () =>
                                      setViewChangeoverId(
                                        item.ShiftchangeOver_ID,
                                      )
                                  : undefined
                              }
                            >
                              {formatBlockTime(item.EndTime)}
                            </td>
                            <td className={`${td} text-[#3e494a]`}>
                              {item.TimesheetID != null
                                ? item.ProjectCode || "—"
                                : ""}
                            </td>
                            <td className={`${td} text-[#3e494a]`}>
                              {item.TimesheetID != null
                                ? item.TestTypeName || "—"
                                : ""}
                            </td>
                            <td
                              className={`${td} text-[#3e494a]`}
                              style={{
                                maxWidth: 130,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item.TimesheetID != null
                                ? item.TestName || "—"
                                : ""}
                            </td>
                            <td className={`${td} text-[#3e494a]`}>
                              {item.TimesheetID != null
                                ? item.ActivityName || "—"
                                : ""}
                            </td>
                            <td className={`${td} text-center text-[#3e494a]`}>
                              {hasLoggedData && item.Temperature != null
                                ? item.Temperature
                                : ""}
                            </td>
                            <td className={`${td} text-center text-[#3e494a]`}>
                              {hasLoggedData && item.Humidity != null
                                ? item.Humidity
                                : ""}
                            </td>
                            <td className={`${td} text-[#3e494a]`}>
                              {item.TimesheetID != null
                                ? item.TestOfficer || "—"
                                : ""}
                            </td>
                            <td className={`${td} text-[#3e494a]`}>
                              {item.TimesheetID != null
                                ? item.OnTraineeEmpName || "—"
                                : ""}
                            </td>
                            <td className={`${td} text-[#3e494a]`}>
                              {item.TimesheetID != null
                                ? item.OnBehalfEmpName || "—"
                                : ""}
                            </td>
                            <td className={`${td} text-center`}>
                              {hasLoggedData &&
                                hasPrivilege(PRIVILEGES.SU_EDIT) && (
                                  <button
                                    onClick={() => setEditEntry(item)}
                                    className="inline-flex items-center justify-center h-6 w-6 rounded-lg transition-all hover:shadow-sm active:scale-95"
                                    style={{
                                      background: "#08757C15",
                                      color: "#08757C",
                                    }}
                                  >
                                    <span className="material-symbols-outlined text-[13px]">
                                      edit
                                    </span>
                                  </button>
                                )}
                            </td>
                            <td
                              className={`${td} text-[#3e494a]`}
                              style={{
                                maxWidth: 130,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item.TimesheetID != null
                                ? item.ProjectName || "—"
                                : ""}
                            </td>
                            <td
                              className={`${td} text-[#3e494a]`}
                              style={{
                                maxWidth: 160,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={
                                item.TimesheetID != null
                                  ? item.Remarks || ""
                                  : ""
                              }
                            >
                              {item.TimesheetID != null
                                ? item.Remarks || "—"
                                : ""}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Temp / Humidity popup — auto-shows on load + manual trigger */}
      {showTempPopup && (
        <TempHumidityPopup
          facility={createData?.facility}
          onSave={handleTempSave}
          onClose={tempLogged ? () => setShowTempPopup(false) : null} // can't dismiss if never logged
        />
      )}
      {drawerOpen && (
        <SetupDrawer
          dateFormat={userDateFormat}
          lab={selectedLab}
          createData={createData}
          timesheetList={timesheetList}
          tempLogged={tempLogged}
          onNeedTempLog={() => setShowTempPopup(true)}
          adhocTesting={adhocTesting}
          onClose={handleDrawerClose}
          onSaved={(
            newStartTime,
            newStartDate,
            entryHours = 0,
            freshCreateData,
          ) => {
            setCreateData((prev) => {
              const base = freshCreateData ?? prev;
              return {
                ...base,
                facility: {
                  ...(prev?.facility ?? {}), // preserve temperatureMin/Max etc. from original
                  ...(base?.facility ?? {}), // apply any fresh facility values
                },
                startTime: newStartTime,
                startDate: newStartDate,
              };
            });
           
            if (newStartDate) currentWorkDateRef.current = newStartDate;

           
            const acts4Hrs = tempHumidityFrequency === "4HRS";
            if (acts4Hrs && accHrsKey && entryHours > 0) {
              const prev = Number(sessionStorage.getItem(accHrsKey) ?? 0);
              const next = prev + entryHours;
              sessionStorage.setItem(accHrsKey, next);
              if (next >= 4) {
                if (storageKey) sessionStorage.removeItem(storageKey);
                setTempLogged(false);
                setLoggedTemp({ temperature: "", humidity: "" });
                setShowTempPopup(true);
              }
            }

           
            if (
              tempHumidityFrequency === "DAY" &&
              tempLoggedDayKey &&
              newStartDate
            ) {
              const lastLoggedDay = sessionStorage.getItem(tempLoggedDayKey);
              const newDay = toDateKey(newStartDate);
              if (newDay && newDay !== lastLoggedDay) {
                if (storageKey) sessionStorage.removeItem(storageKey);
                setTempLogged(false);
                setLoggedTemp({ temperature: "", humidity: "" });
                setShowTempPopup(true);
              }
            }

            if (tempHumidityFrequency === "SHIFT" && tempLoggedShiftKey && newStartTime) {
              const { mins } = parseEntryTime(newStartTime);
              const newShiftId = resolveShiftId(mins, shiftList);
              currentWorkShiftRef.current = newShiftId;
              const lastLoggedShift = sessionStorage.getItem(tempLoggedShiftKey);
              if (
                newShiftId != null &&
                String(newShiftId) !== lastLoggedShift
              ) {
                if (storageKey) sessionStorage.removeItem(storageKey);
                setTempLogged(false);
                setLoggedTemp({ temperature: "", humidity: "" });
                setShowTempPopup(true);
              }
            }
          }}
        />
      )}
      {changeoverOpen && (
        <ShiftChangeOverPanel
          facilityId={selectedLab?.facilityId ?? selectedLab?.id}
          employeeId={employeeID}
          companyId={companyID}
          shiftChangeOverId={0}
          onClose={() => setChangeoverOpen(false)}
          onSaved={() => {
            setChangeoverOpen(false);
            refreshTimesheetList();
          }}
          showToast={showToast}
        />
      )}
      {viewChangeoverId && (
        <ShiftChangeOverPanel
          facilityId={selectedLab?.facilityId ?? selectedLab?.id}
          employeeId={employeeID}
          companyId={companyID}
          shiftChangeOverId={viewChangeoverId}
          onClose={() => setViewChangeoverId(null)}
          onSaved={() => {
            setViewChangeoverId(null);
            refreshTimesheetList();
          }}
          showToast={showToast}
        />
      )}
      {editEntry && createData && (
        <SetupDrawer
          dateFormat={userDateFormat}
          lab={selectedLab}
          createData={createData}
          editEntry={editEntry}
          timesheetList={timesheetList}
          tempLogged={true}
          onNeedTempLog={() => {}}
          onClose={() => setEditEntry(null)}
          onSaved={() => {
            setEditEntry(null);
            refreshTimesheetList();
          }}
        />
      )}
      {viewProject && (
        <ProjectDetailModal
          proj={viewProject}
          lab={selectedLab}
          entryList={entryList}
          createData={createData}
          onClose={() => setViewProject(null)}
          onEntrySaved={refreshTimesheetList}
        />
      )}

      
      {toast && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold
          ${toast.isError ? "bg-[#ba1a1a] text-white" : "bg-[#bbf46e] text-[#112000]"}`}
        >
          <span className="material-symbols-outlined text-[18px]">
            {toast.isError ? "error" : "check_circle"}
          </span>
          {toast.message}
        </div>
      )}
    </DashboardLayout>
  );
}
